import type { CostEntry, EntryCategory, Id } from '@orgus/protocol';
import { COST_CATEGORY_LABEL, ENTRY_CATEGORIES } from '@orgus/protocol';
import { Paperclip } from '@orgus/ui/icons';
import { useEffect, useRef, useState } from 'react';
import { Button, Dialog, ErrorState, Input, Select, toUiError, toast } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { useProjects } from '../../lib/store.ts';
import { dayOf } from './model.ts';
import { RECEIPT_ACCEPT, saveEntry, uploadReceipt } from './useFinance.ts';

export function projectOptions(projects: { id: Id; name: string }[], shared = 'Shared, split by token spend') {
  return [{ value: '', label: shared }, ...projects.map((p) => ({ value: p.id, label: p.name }))];
}

/** Add or change a cost: one-off, or an invoice spread over a period. A receipt can ride along. */
export function EntryDialog({ open, onOpenChange, entry, defaultCategory }: { open: boolean; onOpenChange(v: boolean): void; entry: CostEntry | null; defaultCategory?: EntryCategory }) {
  const projects = useProjects();
  const [category, setCategory] = useState<EntryCategory>('other');
  const [vendor, setVendor] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [projectId, setProjectId] = useState('');
  const [invoice, setInvoice] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setCategory(entry?.category ?? defaultCategory ?? 'other');
    setVendor(entry?.vendor ?? '');
    setDescription(entry?.description ?? '');
    setAmount(entry ? String(entry.amount) : '');
    setFrom(entry?.from ?? dayOf(new Date()));
    setTo(entry && entry.to !== entry.from ? entry.to : '');
    setProjectId(entry?.projectId ?? '');
    setInvoice(entry?.invoiceNumber ?? '');
    setFile(null);
    setError(null);
  }, [open, entry, defaultCategory]);

  const amountNum = Number(amount.replace(/[$,\s]/g, ''));
  const valid = vendor.trim() && Number.isFinite(amountNum) && amountNum > 0 && from && (!to || to >= from);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const saved = await saveEntry(
        { category, vendor, description, amount: amountNum, from, to: to || from, projectId: projectId || null, source: invoice || entry?.source === 'invoice' ? 'invoice' : 'manual', invoiceNumber: invoice || undefined },
        entry?.id,
      );
      if (file) {
        try {
          await uploadReceipt(saved, file);
        } catch (e) {
          const u = toUiError(e, { what: 'The receipt was not attached.' });
          toast('Cost saved, receipt not attached', { tone: 'error', description: `${u.why} Attach it again from the Ledger row.` });
        }
      }
      onOpenChange(false);
    } catch (e) {
      setError(toUiError(e, { what: 'That cost was not saved.' }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      dismissible={!busy}
      title={entry ? `Edit ${entry.vendor}` : 'Add a cost'}
      description="A period spreads the amount evenly over its days."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} disabled={!valid} onClick={() => void submit()}>
            {entry ? 'Save cost' : 'Add cost'}
          </Button>
        </>
      }
    >
      <form
        className="grid grid-cols-2 gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) void submit();
        }}
      >
        <Select label="Category" options={ENTRY_CATEGORIES.map((c) => ({ value: c, label: COST_CATEGORY_LABEL[c] }))} value={category} onValueChange={(v) => setCategory(v as EntryCategory)} />
        <Input label="Amount (USD)" inputMode="decimal" placeholder="25.00" value={amount} onChange={(e) => setAmount(e.target.value)} />
        <Input label="Vendor" placeholder="Cloudflare" value={vendor} onChange={(e) => setVendor(e.target.value)} maxLength={200} />
        <Input label="Invoice number" hint="Optional" value={invoice} onChange={(e) => setInvoice(e.target.value)} maxLength={80} />
        <Input label="What it was for" wrapperClassName="col-span-2" placeholder="Workers Paid plan" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={200} />
        <Input label="Date, or first day" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        <Input label="Last day" type="date" hint="Leave empty for a one-off" value={to} onChange={(e) => setTo(e.target.value)} error={to && from && to < from ? 'The last day is before the first.' : undefined} />
        <Select label="Project" wrapperClassName="col-span-2" options={projectOptions(projects)} value={projectId} onValueChange={setProjectId} />
        <div className="col-span-2 flex items-center gap-3">
          <input
            ref={fileRef}
            type="file"
            accept={RECEIPT_ACCEPT}
            className="sr-only"
            aria-label="Receipt file"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          <Button type="button" size="sm" variant="secondary" icon={<Paperclip />} onClick={() => fileRef.current?.click()}>
            {file ? 'Change receipt' : entry?.receipt ? 'Replace receipt' : 'Attach receipt'}
          </Button>
          <span className="min-w-0 truncate text-sm text-fg-muted">{file ? file.name : entry?.receipt ? entry.receipt.name : 'PDF, PNG, JPEG or WebP, up to 5 MB'}</span>
        </div>
        <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
      </form>
      {error && <ErrorState error={error} variant="inline" className="mt-3" />}
    </Dialog>
  );
}
